import Phaser from 'phaser'
import { GameData } from './GameData'
import { SFXSynth } from './SFXSynth'
import { GAME_CONFIG_DATABASE } from '../data/configDatabase'
import { BGM_FADE_DURATIONS, VOICE_AUDIO_PATH } from '../utils/constants'
import { isSceneAudioHostUsable } from '../utils/sceneLifecycle'

type VolumeControlledSound = Phaser.Sound.BaseSound & {
  setVolume(value: number): Phaser.Sound.BaseSound
}

export class AudioManager {
  private static instance: AudioManager
  private scene: Phaser.Scene | null = null
  private sceneStack: Phaser.Scene[] = []
  private currentBgm: Phaser.Sound.BaseSound | null = null
  private currentBgmKey: string = ''
  private bgmSounds: Set<Phaser.Sound.BaseSound> = new Set()
  private bgmTweenScenes = new WeakMap<Phaser.Sound.BaseSound, Phaser.Scene>()
  private bgmMuted = false
  private sfxMuted = false
  private voiceMuted = false
  private currentVoice: Phaser.Sound.BaseSound | null = null
  private requestedVoiceKey = ''
  private requestedBgmId = ''
  private pendingBgmRequest: { bgmId: string; fadeDuration: number } | null = null
  private pendingBgmUnlockScene: Phaser.Scene | null = null
  private sfxSynth: SFXSynth | null = null
  private sfxSynthUnavailable = false

  private constructor() {}

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager()
    }
    return AudioManager.instance
  }

  setScene(scene: Phaser.Scene): void {
    this.sceneStack = []
    this.scene = scene
  }

  pushScene(scene: Phaser.Scene): void {
    if (this.scene && this.scene !== scene) {
      this.sceneStack.push(this.scene)
    }
    this.scene = scene
  }

  popScene(expectedScene?: Phaser.Scene): void {
    if (expectedScene && this.scene !== expectedScene) return
    this.scene = this.sceneStack.pop() ?? null
  }

  private hasUsableScene(): boolean {
    return isSceneAudioHostUsable(this.scene)
  }

  preload(loader: Phaser.Loader.LoaderPlugin): void {
    const title = GAME_CONFIG_DATABASE.getTable('bgmTracks').title
    if (title) {
      loader.audio(title.key, title.path)
    }
    for (const sfx of Object.values(GAME_CONFIG_DATABASE.getTable('sfxTracks'))) {
      loader.audio(sfx.key, sfx.path)
    }
  }

  playBGM(bgmId: string, fadeDuration: number = BGM_FADE_DURATIONS.DEFAULT_MS): void {
    if (!this.hasUsableScene() || !this.scene) return
    const config = GAME_CONFIG_DATABASE.getTable('bgmTracks')[bgmId]
    if (!config) {
      console.warn(`BGM ${bgmId} not found`)
      return
    }

    if (this.currentBgmKey === bgmId && this.currentBgm?.isPlaying) return

    if (this.queueBgmUntilSoundUnlock(bgmId, fadeDuration)) return

    const gd = GameData.getInstance()
    const masterVol = gd.settings.masterVolume
    const musicVol = gd.settings.musicVolume
    const targetVolume = this.bgmMuted ? 0 : config.volume * masterVol * musicVol

    this.stopBGM(BGM_FADE_DURATIONS.NONE_MS)

    if (!this.scene.cache.audio.exists(config.key)) {
      if (this.requestedBgmId === bgmId) return
      this.requestedBgmId = bgmId
      const hostScene = this.scene
      this.scene.load.audio(config.key, config.path)
      this.scene.load.once(`filecomplete-audio-${config.key}`, () => {
        if (!isSceneAudioHostUsable(hostScene) || this.scene !== hostScene || this.requestedBgmId !== bgmId) return
        this.requestedBgmId = ''
        this.playBGM(bgmId, fadeDuration)
      })
      this.scene.load.once(`loaderror-audio-${config.key}`, () => {
        if (this.requestedBgmId === bgmId) this.requestedBgmId = ''
        console.warn(`Audio ${config.key} failed to load`)
      })
      this.scene.load.start()
      return
    }

    this.currentBgm = this.scene.sound.add(config.key, {
      loop: config.loop,
      volume: 0,
    })
    this.bgmSounds.add(this.currentBgm)
    this.bgmTweenScenes.set(this.currentBgm, this.scene)
    this.currentBgmKey = bgmId
    this.requestedBgmId = ''
    this.currentBgm.play()

    this.scene.tweens.add({
      targets: this.currentBgm,
      volume: targetVolume,
      duration: fadeDuration,
    })
  }

  stopBGM(fadeDuration: number = BGM_FADE_DURATIONS.DEFAULT_MS): void {
    this.clearPendingBgmUnlock()
    if (this.bgmSounds.size === 0 && !this.currentBgm) return
    const sounds = new Set(this.bgmSounds)
    if (this.currentBgm) sounds.add(this.currentBgm)
    for (const bgm of sounds) {
      const isPaused = typeof this.scene?.scene?.isPaused === 'function' && this.scene.scene.isPaused()
      if (!bgm.isPlaying || !this.scene || fadeDuration <= BGM_FADE_DURATIONS.NONE_MS || isPaused) {
        this.destroyBGM(bgm)
        continue
      }
      this.scene.tweens.killTweensOf(bgm)
      this.bgmTweenScenes.set(bgm, this.scene)
      this.scene.tweens.add({
        targets: bgm,
        volume: 0,
        duration: fadeDuration,
        onComplete: () => this.destroyBGM(bgm),
      })
    }
    this.currentBgm = null
    this.currentBgmKey = ''
  }

  private queueBgmUntilSoundUnlock(bgmId: string, fadeDuration: number): boolean {
    if (!this.scene?.sound.locked) return false
    this.pendingBgmRequest = { bgmId, fadeDuration }
    if (this.pendingBgmUnlockScene !== this.scene) {
      this.pendingBgmUnlockScene?.sound.off(Phaser.Sound.Events.UNLOCKED, this.handleSoundUnlocked)
      this.pendingBgmUnlockScene = this.scene
      this.scene.sound.once(Phaser.Sound.Events.UNLOCKED, this.handleSoundUnlocked)
    }
    return true
  }

  private clearPendingBgmUnlock(): void {
    this.pendingBgmRequest = null
    this.pendingBgmUnlockScene?.sound.off(Phaser.Sound.Events.UNLOCKED, this.handleSoundUnlocked)
    this.pendingBgmUnlockScene = null
  }

  private handleSoundUnlocked = (): void => {
    const pending = this.pendingBgmRequest
    this.pendingBgmRequest = null
    this.pendingBgmUnlockScene = null
    if (pending) this.playBGM(pending.bgmId, pending.fadeDuration)
  }

  private destroyBGM(bgm: Phaser.Sound.BaseSound): void {
    const tweenScene = this.bgmTweenScenes.get(bgm)
    tweenScene?.tweens.killTweensOf(bgm)
    if (this.scene && this.scene !== tweenScene) this.scene.tweens.killTweensOf(bgm)
    if (bgm.isPlaying) bgm.stop()
    bgm.destroy()
    this.bgmTweenScenes.delete(bgm)
    this.bgmSounds.delete(bgm)
    if (this.currentBgm === bgm) {
      this.currentBgm = null
      this.currentBgmKey = ''
    }
  }

  playSFX(sfxId: string): void {
    if (this.sfxMuted) return
    const config = GAME_CONFIG_DATABASE.getTable('sfxTracks')[sfxId]
    if (!config) {
      console.warn(`SFX ${sfxId} not found`)
      return
    }

    const gd = GameData.getInstance()
    const masterVol = gd.settings.masterVolume
    const sfxVol = gd.settings.sfxVolume
    const volume = config.volume * masterVol * sfxVol

    if (this.scene?.cache.audio.exists(config.key)) {
      this.scene.sound.play(config.key, { volume })
      return
    }

    // Fallback to synthesized SFX
    const sfxSynth = this.getSfxSynth()
    if (!sfxSynth) return
    switch (sfxId) {
      case 'cursor': sfxSynth.playCursor(); break
      case 'confirm': sfxSynth.playConfirm(); break
      case 'cancel': sfxSynth.playCancel(); break
      case 'battle_start': sfxSynth.playEncounter(); break
      case 'attack_hit': sfxSynth.playAttackHit(); break
      case 'attack_slash': sfxSynth.playAttackSlash(); break
      case 'magic_cast': sfxSynth.playMagicCast(); break
      case 'heal': sfxSynth.playHeal(); break
      case 'item_use': sfxSynth.playItemUse(); break
      case 'level_up': sfxSynth.playLevelUp(); break
      case 'open_menu': sfxSynth.playOpenMenu(); break
      case 'close_menu': sfxSynth.playCloseMenu(); break
      case 'equip': sfxSynth.playEquip(); break
      case 'get_item': sfxSynth.playGetItem(); break
      case 'encounter': sfxSynth.playEncounter(); break
      case 'step_grass':
      case 'step_stone': sfxSynth.playStep(); break
      case 'warp': sfxSynth.playWarp(); break
      case 'victory_fanfare': sfxSynth.playLevelUp(); break
      case 'dialogue_advance': sfxSynth.playDialogue(); break
      default: console.warn(`No synth fallback for SFX ${sfxId}`)
    }
  }

  private getSfxSynth(): SFXSynth | null {
    if (this.sfxSynth || this.sfxSynthUnavailable) return this.sfxSynth
    try {
      this.sfxSynth = new SFXSynth()
    } catch {
      this.sfxSynthUnavailable = true
    }
    return this.sfxSynth
  }

  playVoice(voiceKey: string, _text: string): void {
    if (!this.hasUsableScene() || !this.scene || this.voiceMuted) return
    this.stopVoice()

    const path = `${VOICE_AUDIO_PATH.DIRECTORY}/${voiceKey}${VOICE_AUDIO_PATH.EXTENSION}`
    const key = `voice_${voiceKey}`
    this.requestedVoiceKey = key

    if (!this.scene.cache.audio.exists(key)) {
      const hostScene = this.scene
      this.scene.load.audio(key, path)
      this.scene.load.once(`filecomplete-audio-${key}`, () => {
        if (!isSceneAudioHostUsable(hostScene) || this.scene !== hostScene || this.requestedVoiceKey !== key) return
        this.requestedVoiceKey = ''
        this.playLoadedVoice(key)
      })
      this.scene.load.once(`loaderror-audio-${key}`, () => {
        if (this.requestedVoiceKey === key) this.requestedVoiceKey = ''
      })
      this.scene.load.start()
    } else {
      this.requestedVoiceKey = ''
      this.playLoadedVoice(key)
    }
  }

  private getVoiceVolume(): number {
    const gd = GameData.getInstance()
    return gd.settings.uiVolume * gd.settings.masterVolume
  }

  private playLoadedVoice(key: string): void {
    if (!this.hasUsableScene() || !this.scene) return
    const voice = this.scene.sound.add(key, { volume: this.getVoiceVolume(), loop: false })
    this.currentVoice = voice
    voice.once('complete', () => {
      if (this.currentVoice === voice) this.currentVoice = null
      voice.destroy()
    })
    voice.play()
  }

  stopVoice(): void {
    if (this.currentVoice?.isPlaying) {
      this.currentVoice.stop()
    }
    this.currentVoice?.destroy()
    this.currentVoice = null
    this.requestedVoiceKey = ''
  }

  playBGMForMap(mapId: string): void {
    const bgmId = GAME_CONFIG_DATABASE.getTable('maps')[mapId]?.bgm ?? GAME_CONFIG_DATABASE.getTable('mapBgm')[mapId]
    if (bgmId) {
      this.playBGM(bgmId)
    }
  }

  playBattleBGM(isBoss: boolean): void {
    this.playBGM(isBoss ? 'battle_boss' : 'battle_normal')
  }

  playVictoryBGM(): void {
    this.playBGM('victory', BGM_FADE_DURATIONS.FAST_MS)
  }

  playGameOverBGM(): void {
    this.playBGM('game_over', BGM_FADE_DURATIONS.FAST_MS)
  }

  updateVolume(): void {
    if (this.currentBgm) {
      const gd = GameData.getInstance()
      const config = GAME_CONFIG_DATABASE.getTable('bgmTracks')[this.currentBgmKey]
      if (config) {
        const masterVol = gd.settings.masterVolume
        const musicVol = gd.settings.musicVolume
        const targetVolume = this.bgmMuted ? 0 : config.volume * masterVol * musicVol
        ;(this.currentBgm as VolumeControlledSound).setVolume(targetVolume)
      }
    }
    if (this.currentVoice) {
      ;(this.currentVoice as VolumeControlledSound).setVolume(this.voiceMuted ? 0 : this.getVoiceVolume())
    }
  }

  setBGMMuted(muted: boolean): void {
    this.bgmMuted = muted
    this.updateVolume()
  }

  setSFXMuted(muted: boolean): void {
    this.sfxMuted = muted
  }

  setVoiceMuted(muted: boolean): void {
    this.voiceMuted = muted
    if (muted) this.stopVoice()
  }

  resumeBGM(): void {
    if (this.currentBgm && !this.currentBgm.isPlaying && !this.bgmMuted) {
      this.currentBgm.play()
    }
  }

  pauseBGM(): void {
    if (this.currentBgm?.isPlaying) {
      this.currentBgm.pause()
    }
  }
}
